import type { FloorId } from '@/data/floors';

/**
 * Bygningsdata for Forlagshuset (fase 2, Mad TV-mekanikk): hvilke rom
 * som ligger på hvilken fysisk etasje. Heisen og korridorene leser
 * herfra — aldri hardkod etasjefordeling i scener.
 *
 * Etasje 7 er reservert konkurrenten Kapelle Damm: korridoren finnes,
 * men døra er låst (fase 5 åpner lokalene).
 */

export type EtasjeNr = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/**
 * Sone i BAKGRUNNSBILDETS koordinater som andel 0–1 av bildet —
 * overlever skalering. Scenen mapper til skjerm via bg→skjerm-hjelperen.
 */
export interface SoneRel {
  x: number;
  y: number;
  bredde: number;
  hoyde: number;
}

/** Gulvets gangbane + perspektivskala, i bakgrunnsandeler. */
export interface GangsoneDef {
  /** Der gulvet møter veggen/listverket (bak i rommet). */
  minY: number;
  /** Nederste synlige gulvkant (forrest). */
  maxY: number;
  /** Avatar-skala bakerst (ved minY). */
  skalaMin: number;
  /** Avatar-skala forrest (ved maxY). */
  skalaMax: number;
}

export interface KorridorDef {
  /** Den malte heisdøra. */
  heis: SoneRel;
  /** Malte romdører fra venstre: rom[i] ↔ dorSoner[i], låste pynte-dører etterpå. */
  dorSoner: SoneRel[];
  gangsone: GangsoneDef;
}

/**
 * Hitboxer satt manuelt over de MALTE dørene i korridor-teksturene.
 * Standard: metall-heisdør til venstre + to tredører. Gulvbanen starter
 * ved den vinrøde gulvlisten — ikke ved dørenes underkant.
 */
const STANDARD_KORRIDOR: KorridorDef = {
  heis: { x: 0.052, y: 0.583, bredde: 0.185, hoyde: 0.191 },
  dorSoner: [
    { x: 0.352, y: 0.583, bredde: 0.213, hoyde: 0.191 },
    { x: 0.689, y: 0.583, bredde: 0.209, hoyde: 0.191 },
  ],
  gangsone: { minY: 0.79, maxY: 0.94, skalaMin: 0.75, skalaMax: 1.1 },
};

/** Kapelle Damm: heis + tre innglassede dører, marmor-gulv. */
const KAPELLEDAMM_KORRIDOR: KorridorDef = {
  heis: { x: 0.037, y: 0.562, bredde: 0.204, hoyde: 0.217 },
  dorSoner: [
    { x: 0.278, y: 0.562, bredde: 0.259, hoyde: 0.217 },
    { x: 0.574, y: 0.562, bredde: 0.222, hoyde: 0.217 },
    { x: 0.807, y: 0.562, bredde: 0.174, hoyde: 0.217 },
  ],
  gangsone: { minY: 0.79, maxY: 0.94, skalaMin: 0.75, skalaMax: 1.1 },
};

export interface EtasjeDef {
  nr: EtasjeNr;
  navn: string;
  /** Rom spilleren kan gå inn i fra korridoren. Tom = ingen åpne dører. */
  rom: FloorId[];
  beskrivelse: string;
  /** Dører som vises i korridoren men ikke kan åpnes (ennå). */
  laasteDorer?: Array<{ label: string; hint: string }>;
  /** Soner over de malte dørene + gangbane i korridor-teksturen. */
  korridor: KorridorDef;
}

/** Sortert fra øverste til nederste etasje — samme rekkefølge som heispanelet. */
export const BYGNING: EtasjeDef[] = [
  {
    nr: 8,
    navn: 'Takterrassen',
    rom: ['tak'],
    beskrivelse: 'Premium-avtaler og retreater.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 7,
    navn: 'Kapelle Damm',
    rom: ['kapelleResepsjon'],
    beskrivelse: 'Konkurrenten leier hele etasjen. Dyrere luft, kaldere lys.',
    laasteDorer: [
      {
        label: 'Redaktørkontor',
        hint: 'Låst. Kun etter invitasjon.',
      },
      {
        label: 'Møterom',
        hint: 'Låst. Gjennom glasset: en hestesko av tomme stoler.',
      },
    ],
    korridor: KAPELLEDAMM_KORRIDOR,
  },
  {
    nr: 6,
    navn: 'Direksjonen',
    rom: ['direksjon'],
    beskrivelse: 'Strategi og budsjett.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 5,
    navn: 'Redaksjonen',
    rom: ['redaksjon', 'mittKontor'],
    beskrivelse: 'Manus, redaktører og ditt eget kontor.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 4,
    navn: 'Design',
    rom: ['design'],
    beskrivelse: 'Cover og typografi.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 3,
    navn: 'Marked og PR',
    rom: ['marked'],
    beskrivelse: 'Kampanjer og presse.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 2,
    navn: 'Resepsjonen',
    rom: ['resepsjon', 'kantine'],
    beskrivelse: 'Besøk, beskjeder og kantina.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 1,
    navn: 'Bokhandelen',
    rom: ['bokhandel'],
    beskrivelse: 'Salg og foaje.',
    korridor: STANDARD_KORRIDOR,
  },
  {
    nr: 0,
    navn: 'Kjelleren',
    rom: ['kjeller'],
    beskrivelse: 'Trykkeriet.',
    korridor: STANDARD_KORRIDOR,
  },
];

export function etasjeFor(rom: FloorId): EtasjeDef | undefined {
  return BYGNING.find((e) => e.rom.includes(rom));
}

export function finnEtasje(nr: number): EtasjeDef | undefined {
  return BYGNING.find((e) => e.nr === nr);
}

export function romPaaEtasje(nr: number): FloorId[] {
  return finnEtasje(nr)?.rom ?? [];
}

export function andreRomPaaSammeEtasje(rom: FloorId): FloorId[] {
  const etasje = etasjeFor(rom);
  if (!etasje) return [];
  return etasje.rom.filter((r) => r !== rom);
}

/** Avledet rom → etasjenummer-kart. timeCosts bruker dette for heisavstand. */
export function byggEtasjeNummer(): Record<FloorId, number> {
  const kart = {} as Record<FloorId, number>;
  for (const etasje of BYGNING) {
    for (const rom of etasje.rom) {
      kart[rom] = etasje.nr;
    }
  }
  return kart;
}
